/**
 * The Guides outlines (1.7.0 stream B): for the V1 clip under the playhead, each format's crop window mapped into the
 * primary picture through the primary's own crop (or Fit rectangle). Pure (apps/web/test/monitor-170.test.ts).
 */
import { PRIMARY_FORMAT, cropAt, framingAt, resolveAt, type CropWindow, type FormatRef, type ResolvedFormat, type SequenceDocument, type Size } from '@prism/contracts';
import { centredBox, outlineInPrimary, sourceInFrame, type FracRect } from './monitor-geometry';

export interface GuideOutline {
  format: ResolvedFormat;
  /** The outline as fractions of the primary frame (may reach outside 0…1). */
  rect: FracRect;
  /** The V1 clip it belongs to, or null when there is none at the playhead (a centred box of the format's shape). */
  clipId: string | null;
  sourceTicks: number;
  /** The format's crop window of the source (null: Fit or Blur, the whole picture). */
  win: CropWindow | null;
  /** Where the primary shows the source (fractions of the primary frame). */
  place: FracRect;
  sourceAspect: number;
  zoom: number;
}

/**
 * The outlines of `formats` (the primary is skipped) at `ph`. `docs` holds each format's document (documentForFormat,
 * 'primary' included); `sourceSizes` the sources' picture sizes by version id.
 */
export function guideOutlines(docs: Map<FormatRef, SequenceDocument>, formats: ResolvedFormat[], ph: number, sourceSizes: Map<string, Size>): GuideOutline[] {
  const pd = docs.get(PRIMARY_FORMAT);
  if (!pd) return [];
  const pa = pd.width / pd.height;
  const hit = resolveAt(pd, ph);
  const size = hit ? sourceSizes.get(hit.placed.clip.versionId) : undefined;
  const out: GuideOutline[] = [];
  for (const f of formats) {
    if (f.isPrimary) continue;
    const fa = f.width / f.height;
    if (!hit || !size) {
      out.push({ format: f, rect: centredBox(fa, pa), clipId: null, sourceTicks: 0, win: null, place: { x: 0, y: 0, w: 1, h: 1 }, sourceAspect: pa, zoom: 1 });
      continue;
    }
    const sa = size.width / size.height;
    const st = hit.sourceTicks;
    const place = sourceInFrame(cropAt(sa, pa, hit.placed.clip, st), sa, pa);
    const clip = docs.get(f.id)?.video.find((c) => c.id === hit.placed.clip.id) ?? hit.placed.clip;
    const win = cropAt(sa, fa, clip, st);
    out.push({ format: f, rect: outlineInPrimary(win, place), clipId: clip.id, sourceTicks: st, win, place, sourceAspect: sa, zoom: framingAt(clip, st).zoom });
  }
  return out;
}
